import { FilterSelect } from '../components/FilterSelect';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import type { ChatResponse } from '../types';
import { asNumber, num, uniq } from '../utils';

interface ChatFilters {
  store: string;
  contact: string;
  status: string;
  linked: string;
}

interface ChatScreenProps {
  data: ChatResponse | null;
  loaded: boolean;
  busy: boolean;
  filters: ChatFilters;
  setFilters: (filters: ChatFilters) => void;
}

export function ChatScreen({ data, loaded, busy, filters, setFilters }: ChatScreenProps) {
  if (busy && !loaded) return <div className="card empty">Đang tải hội thoại VPage...</div>;
  if (!loaded) return <div className="card empty">Mở Đối soát chăm sóc để tải hội thoại.</div>;
  const dashboard = data?.dashboard || {};
  const all = data?.conversations || [];
  const stores = uniq([...(data?.store || []).map(row => row.store_code), ...all.map(row => row.store_code || '')]);
  const patch = (update: Partial<ChatFilters>) => setFilters({ ...filters, ...update });
  const rows = all.filter(row =>
    (!filters.store || row.store_code === filters.store)
    && (!filters.contact || (filters.contact === 'phone' ? Boolean(row.eff_phone) : !row.eff_phone))
    && (!filters.status || row.derived_care_status === filters.status)
    && (!filters.linked || (filters.linked === 'lead' ? row.linked_lead : filters.linked === 'order' ? row.has_order_code : filters.linked === 'buyer' ? row.is_buyer : true)));
  const statusTone = (status?: string): 'green' | 'amber' | 'blue' | 'gray' =>
    status === 'ordered' ? 'green' : status === 'awaiting_shop' ? 'amber' : status === 'in_conversation' ? 'blue' : 'gray';

  return (
    <>
      <section className="grid kpis">
        <KpiCard label="Hội thoại" value={num(dashboard.conversations)} sub="NCV + HHT" />
        <KpiCard label="Đã phản hồi" value={num(dashboard.replied)} sub="Cửa hàng đã trả lời" tone="green" />
        <KpiCard label="Biết SĐT" value={num(dashboard.phone_known)} sub={`VPage gắn cờ ${num(dashboard.has_phone_flag)}`} tone="blue" />
        <KpiCard label="Có mã đơn" value={num(dashboard.has_order_code)} sub="Mã đơn được ghi nhận trong hội thoại" tone="violet" />
        <KpiCard label="Khớp khách tiềm năng" value={num(dashboard.linked_to_lead)} sub="Theo mã đơn hoặc SĐT" tone="amber" />
        <KpiCard label="Đã mua hàng" value={num(dashboard.linked_to_buyer)} sub="Đối chiếu theo SĐT" tone="green" />
      </section>

      <section className="filters">
        <div className="actions start">
          <span className="kpi-label">Store</span>
          <button className={`tab ${!filters.store ? 'active' : ''}`} onClick={() => patch({ store: '' })} type="button">Tất cả</button>
          {stores.map(store => <button key={store} className={`tab ${filters.store === store ? 'active' : ''}`} onClick={() => patch({ store })} type="button">{store}</button>)}
        </div>
        <div className="filter-grid">
          <FilterSelect label="Liên hệ" value={filters.contact} onChange={contact => patch({ contact })} options={['phone', 'no_phone']} />
          <FilterSelect label="Trạng thái chat" value={filters.status} onChange={status => patch({ status })} options={['ordered', 'awaiting_shop', 'in_conversation', 'new_unanswered']} />
          <FilterSelect label="Đã đối soát" value={filters.linked} onChange={linked => patch({ linked })} options={['lead', 'order', 'buyer']} />
        </div>
      </section>

      <section className="card">
        <div className="section-title"><h2>Hội thoại VPage đã đối soát</h2><span>{num(rows.length)} / {num(all.length)} hội thoại · đối chiếu SĐT, khách hàng và đơn hàng</span></div>
        <div className="scroll">
          <table>
            <thead><tr><th>Cập nhật</th><th>Cửa hàng</th><th>Khách</th><th>SĐT</th><th>Trạng thái</th><th>Mã đơn</th><th>Khách tiềm năng</th><th>Có ghi nhận tư vấn</th><th>Đã mua</th><th>Tin cuối</th></tr></thead>
            <tbody>{rows.slice(0, 600).map(row => (
              <tr key={row.conversation_id}>
                <td className="tnum muted">{row.updated_date}</td>
                <td><b>{row.store_code}</b></td>
                <td>{row.customer_name || '-'}</td>
                <td className="tnum">{row.eff_phone || (row.has_phone ? <span className="muted">Zalo có</span> : '-')}</td>
                <td><Tag tone={statusTone(row.derived_care_status)}>{row.derived_care_status || '-'}</Tag></td>
                <td className="tnum">{row.nhanh_code || '-'}</td>
                <td>{row.linked_lead ? <Tag tone="green">{row.lead_status || 'lead'}</Tag> : '-'}</td>
                <td className="tnum">{row.linked_capture_id ? '✓' : '-'}</td>
                <td>{row.is_buyer ? <Tag tone="green">{num(asNumber(row.fsales_orders))} đơn</Tag> : '-'}</td>
                <td className="need">{row.last_message}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>
    </>
  );
}
